import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  People,
  SportsTennis,
  EmojiEvents,
  Score,
  Timeline,
} from '@mui/icons-material';
import apiService from '../../services/api';

interface AnalyticsData {
  total_users: number;
  total_matches: number;
  total_tournaments: number;
  total_courts: number;
  total_clubs: number;
  active_matches: number;
  completed_matches: number;
  pending_matches: number;
  period_matches: number;
  period_users: number;
  total_scores: number;
  average_win_rate: number;
  most_active_user: string;
  most_winning_user: string;
  recent_activity: Array<{
    type: string;
    user: string;
    time: string;
    details: string;
  }>;
  period: string;
}

interface UserStats {
  users: Array<{
    id: number;
    name: string;
    email: string;
    created_at: string;
    total_matches: number;
    completed_matches: number;
    active_matches: number;
    win_rate: number;
  }>;
  total_count: number;
  period: string;
}

interface MatchStats {
  matches: Array<{
    id: number;
    status: string;
    created_at: string;
    participants_count: number;
    court_id: number;
    tournament_id: number;
  }>;
  total_count: number;
  status_distribution: Record<string, number>;
  period: string;
}

export const AdminAnalyticsPage: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [matchStats, setMatchStats] = useState<MatchStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('all_time');

  useEffect(() => {
    fetchAnalyticsData();
  }, [selectedPeriod]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Fetch main analytics
      const analyticsResponse = await apiService.getAdminStats(selectedPeriod);
      setAnalyticsData(analyticsResponse.data);

      // Fetch user statistics
      const userResponse = await apiService.getAdminStatsUsers(selectedPeriod);
      setUserStats(userResponse.data as any);

      // Fetch match statistics
      const matchResponse = await apiService.getAdminStatsMatches(selectedPeriod);
      setMatchStats(matchResponse.data as any);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'match_created':
        return <SportsTennis />;
      case 'user_registered':
        return <People />;
      case 'tournament_started':
        return <EmojiEvents />;
      default:
        return <Timeline />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'match_created':
        return 'primary';
      case 'user_registered':
        return 'success';
      case 'tournament_started':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Analytics Dashboard
        </Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={selectedPeriod}
            label="Period"
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            <MenuItem value="week">Last Week</MenuItem>
            <MenuItem value="month">Last Month</MenuItem>
            <MenuItem value="all_time">All Time</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Overview Cards */}
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <People color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{analyticsData?.total_users || 0}</Typography>
                  <Typography color="textSecondary">Total Users</Typography>
                  <Typography variant="caption" color="success.main">
                    +{analyticsData?.period_users || 0} this period
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SportsTennis color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{analyticsData?.total_matches || 0}</Typography>
                  <Typography color="textSecondary">Total Matches</Typography>
                  <Typography variant="caption" color="success.main">
                    +{analyticsData?.period_matches || 0} this period
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <EmojiEvents color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{analyticsData?.total_tournaments || 0}</Typography>
                  <Typography color="textSecondary">Tournaments</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Score color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{analyticsData?.average_win_rate || 0}%</Typography>
                  <Typography color="textSecondary">Avg Win Rate</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Match Status Distribution */}
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
        <Box flex={1}>
          <Card>
            <CardHeader title="Match Status Distribution" />
            <CardContent>
              {matchStats?.status_distribution && Object.entries(matchStats.status_distribution).map(([status, count]) => (
                <Box key={status} mb={2}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" textTransform="capitalize">
                      {status.replace('_', ' ')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {count}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(count / (matchStats.total_count || 1)) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardHeader title="Top Performers" />
            <CardContent>
              <Box mb={2}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Most Active Player
                </Typography>
                <Typography variant="body2">
                  {analyticsData?.most_active_user || 'No data'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="success.main" gutterBottom>
                  Most Winning Player
                </Typography>
                <Typography variant="body2">
                  {analyticsData?.most_winning_user || 'No data'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Recent Activity */}
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
        <Box flex={1}>
          <Card>
            <CardHeader title="Recent Activity" />
            <CardContent>
              <List>
                {analyticsData?.recent_activity?.slice(0, 5).map((activity, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemIcon>
                        <Chip
                          icon={getActivityIcon(activity.type)}
                          label={activity.type.replace('_', ' ')}
                          color={getActivityColor(activity.type) as any}
                          size="small"
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={activity.details}
                        secondary={`${activity.user} • ${new Date(activity.time).toLocaleString()}`}
                      />
                    </ListItem>
                    {index < 4 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardHeader title="Top Users by Activity" />
            <CardContent>
              <List>
                {userStats?.users?.slice(0, 5).map((user, index) => (
                  <React.Fragment key={user.id}>
                    <ListItem>
                      <ListItemIcon>
                        <People color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={user.name}
                        secondary={`${user.total_matches} matches • ${user.win_rate}% win rate`}
                      />
                    </ListItem>
                    {index < 4 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};
